import Core from '../plugin-core-system/core.class.js';
import PluginController from '../plugin-core-system/plugin.controller.js';
import CssPropertyHelper from '../helper/css-properties.helper.js';
import CsCookieBannerRenderer from '../plugins/cs-cookie-banner-renderer.js';
import Cookies from 'js-cookie';

export default class CsCookieBannerHandler extends Core {

  /**
   * options
   */
  static options = {

    modalName: 'cscb-cookie-banner', // must match the name in the template (trigger and modal)
    openModalOnStart: true,
    openModalOnExternalMediaFrame: true,
    openModalIgnoreUrlList: ['intranet.bbw-leipzig.de', 'bbw-intranet-13.ddev.site'],
    cookieName: 'cs-cookie-banner',
    expireDays: 30,
    gtmCookieText: 'gtm',
    gtmMainCategory: 'statistics',
    loadGtmConfigErrMsg: 'Cookie Banner: Fehler beim Laden der GTM-JSON-Konfiguration',
    path: {
      gtmConfigJson: '/_assets/7b3ba8bb3871137dffa4d9cccacf730f/JavaScript/config/config-gtm-domain-keys.json',
    },
    selector: {
      firstFocusableElement: 'body a:first-of-type',
      contrastButtonPage: '.js-toggle-high-contrast',
      showClass: 'h-show',
      animationClass: 'h-animate',
      cookieBannerModal: '.js-cscb-edit-box',
      cookieBannerLayer: '.js-cscb-info-box',
      contrastButton: '.js-cscb-toggle-high-contrast',
      checkbox: '.js-cscb-checkbox',
      checkboxParentClass: 'js-cscb-checkbox--parent',
      checkboxChildClass: 'js-cscb-checkbox--child',
      acceptAllCookies: '.js-cscb-accept-all-cookies',
      declineAllCookies: '.js-cscb-decline-cookies',
      customizeSettings: '.js-cscb-open-edit-box',
      saveSettings: '.js-cscb-save-cookie-settings',
      externalMedia: '.js-cscb-external-media',
      externalMediaWrapper: '.js-cscb-preview-box-wrapper',
      externalMediaWrapperPreviewBoxIsVisibleClass: 'preview-box-is-visible',
      externalMediaPreviewBoxClass: 'js-cscb-preview-box',
      externalMediaAcceptButtonClass: 'js-cscb-preview-box-button',
      externalMediaAccordionTriggerId: 'cscb-accordion-trigger-externalMedia',
      externalMediaLoadingClass: 'has-loader',
      externalMediaLiveRegion: '.js-cscb-preview-box-live-region'
    },
    cssVariables: {
      cookieBannerAnimationTime: '--cscb-default-transition-time'
    }
  };



  /**
   * helper functions
   */
  reserveSpaceForCookieBannerLayer(stateOpen = true) {
    const cookieBannerLayer = document.querySelector(CsCookieBannerHandler.options.selector.cookieBannerLayer);
    const bodyOffset = stateOpen ? cookieBannerLayer.offsetHeight : 0;
    const body = document.querySelector('body');
    if (body) {
      body.style.marginBottom = `${bodyOffset}px`;
    }
  }

  isUrlOnIgnoreList() {
    const ignoreList = CsCookieBannerHandler.options.openModalIgnoreUrlList;
    if (!Array.isArray(ignoreList) || ignoreList.length === 0) return false;

    const currentUrl = window.location.href;
    return ignoreList.some(url => currentUrl.includes(url));
  }



  /**
   * cookie settings - init content
   */
  initializeAccordion() {

    const accordionSelector = CsCookieBannerRenderer.options.selector.accordionClasses.map(cls => `.${cls}`).join('');
    const accordionNode = document.querySelector(accordionSelector);
    if (accordionNode) {
      PluginController.initializePlugin('CsAccordion', accordionNode);
    }
  }

  initializeCheckboxes() {
    const cookieString = Cookies.get(CsCookieBannerHandler.options.cookieName);
    if (!cookieString) return;

    const values = cookieString.split('|').filter(key => key && key !== 'gtm');

    values.forEach(key => {
      const checkbox = document.getElementById(`cscb-${key}`);
      if (checkbox) {
        checkbox.checked = true;
      }
    });
  }

  initializeCookieBannerContent() {
    this.initializeAccordion();
    this.initializeCheckboxes();
  }



  /**
   * cookie settings - checkbox handling
   */
  handleCheckboxChildrenStatePropagation(target) {
    const container = target.closest('.' + CsCookieBannerRenderer.options.selector.checkboxContainerClass);
    const childCheckboxes = container?.querySelectorAll('.' + CsCookieBannerHandler.options.selector.checkboxChildClass);
    if (childCheckboxes?.length) {
      childCheckboxes.forEach(child => {
        child.checked = target.checked;
      });
    }
  }

  handleCheckboxParentStatePropagation(target) {
    const wrapper = target.closest('.' + CsCookieBannerRenderer.options.selector.checkboxContainerClass);
    if (!wrapper) return;

    const parentCheckbox = wrapper.querySelector('.' + CsCookieBannerHandler.options.selector.checkboxParentClass);
    const childCheckboxes = wrapper.querySelectorAll('.' + CsCookieBannerHandler.options.selector.checkboxChildClass);

    if (!parentCheckbox || !childCheckboxes.length) return;

    const allUnchecked = Array.from(childCheckboxes).every(cb => !cb.checked);

    parentCheckbox.checked = !allUnchecked;
  }

  activateAllCheckboxes() {
    const checkboxes = document.querySelectorAll(CsCookieBannerHandler.options.selector.checkbox);
    checkboxes.forEach(cb => cb.checked = true);
  }

  deactivateAllCheckboxes() {
    const checkboxes = document.querySelectorAll(CsCookieBannerHandler.options.selector.checkbox);
    checkboxes.forEach(cb => {
      if (cb.id !== 'cscb-essential') {
        cb.checked = false;
      }
    });
  }



  /**
   * google tag manager (gtm) handling
   */
  async loadGtmSettings() {
    const response = await fetch(CsCookieBannerHandler.options.path.gtmConfigJson);
    if (!response.ok) throw new Error(CsCookieBannerHandler.options.loadGtmConfigErrMsg);

    const configData = await response.json();

    this.gtmData = configData['googleTagManagerKeys'];
  }

  initDataLayer() {
    if (this._tagManagerDataLayerSet) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };
    this._tagManagerDataLayerSet = true;
  }

  setGoogleTag(method = 'default', value = 'denied', command = 'consent') {
    // eslint-disable-next-line
    gtag(command, method, {
      ad_user_data: value,
      ad_personalization: value,
      ad_storage: value,
      analytics_storage: value
    });
  }

  setTagManagerDefaults() {
    this.initDataLayer();
    if (!this._consentDefaultSet) {
      this.setGoogleTag();
      this._consentDefaultSet = true;
    }
  }

  revokeTagManager() {
    this.initDataLayer();
    this.setGoogleTag('update', 'denied');
    this._gtmLoaded = false;
  }

  loadGtmScript(gtmContainerId) {
    if (this._gtmLoaded || document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) return;

    this.initDataLayer();
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmContainerId)}`;
    document.head.appendChild(s);

    this._gtmLoaded = true;
  }

  loadTagManager() {

    const { cookieName, gtmCookieText } = CsCookieBannerHandler.options;

    // set defaults
    this.setTagManagerDefaults();

    // check if user accepted gtm cookie
    const cookieString = Cookies.get(cookieName) || '';
    const hasConsent = cookieString.includes(gtmCookieText);

    // no consent
    if (!hasConsent) {
      this.revokeTagManager();
      return;
    }

    // set consent
    this.setGoogleTag('update', 'granted');

    // get gtm container id
    let domain = window.location.hostname;
    const gtmContainerId = this.gtmData?.[domain] || null;
    if (!gtmContainerId || !/^GTM-\w+/.test(gtmContainerId)) {
      console.warn('GTM container ID ist ungültig oder fehlt: ', gtmContainerId);
      return;
    }

    // load gtm script
    this.loadGtmScript(gtmContainerId);
  }



  /**
   * external media content
   */
  screenreaderAnnouncement(wrapper, message = '') {
    const region = wrapper?.querySelector(CsCookieBannerHandler.options.selector.externalMediaLiveRegion);
    if (!region) return;

    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  }

  showExternalMediaContent(externalMediaElement) {
    if (!externalMediaElement) return;

    const wrapper = externalMediaElement.closest(CsCookieBannerHandler.options.selector.externalMediaWrapper);
    const message = wrapper?.dataset.srActivateMessage;
    const isIframe = externalMediaElement instanceof HTMLIFrameElement || externalMediaElement.tagName === 'IFRAME';

    // add loading state while the external media element is fetching its content
    if (wrapper) wrapper.classList.add(CsCookieBannerHandler.options.selector.externalMediaLoadingClass);

    if (isIframe) {
      const src = externalMediaElement.dataset.src;
      if (!src) return;

      // remove loading class once the external media element has finished loading
      externalMediaElement.addEventListener('load', () => {

        // remove loading class
        if (wrapper) wrapper.classList.remove(CsCookieBannerHandler.options.selector.externalMediaLoadingClass);

        // include in focus tab order
        externalMediaElement.tabIndex = 0;

        // live region announcement
        this.screenreaderAnnouncement(wrapper, message);

        // focus on iframe
        externalMediaElement.focus();
      }, { once: true }); // prevents stacking multiple listeners on repeated calls

      // set src from data-src
      externalMediaElement.setAttribute('src', src);
      externalMediaElement.removeAttribute('data-src');

      // show the iframe
      externalMediaElement.hidden = false;
    } else {
      // remove loading class
      if (wrapper) wrapper.classList.remove(CsCookieBannerHandler.options.selector.externalMediaLoadingClass);

      // live region announcement
      this.screenreaderAnnouncement(wrapper, message);

      // show the external media element
      externalMediaElement.hidden = false;
    }
  }

  removeExternalMediaContent(externalMediaElement) {

    // for iframes
    if (externalMediaElement.hasAttribute('src')) {
      // remove src and set data-src (e.g. if iframe)
      const src = externalMediaElement.getAttribute('src');
      externalMediaElement.setAttribute('data-src', src);
      externalMediaElement.removeAttribute('src');

      externalMediaElement.tabIndex = -1;
    }

    // hide the external media element
    externalMediaElement.hidden = true;
  }

  refreshExternalMediaContent() {
    const cookieString = Cookies.get(CsCookieBannerHandler.options.cookieName);

    const externalMediaElements = document.querySelectorAll(CsCookieBannerHandler.options.selector.externalMedia);

    externalMediaElements.forEach(externalMediaElement => {
      const cookieName = externalMediaElement.dataset.cookieName;
      if (!cookieName) {
        console.warn('No cookie name defined for media element (e.g. check "CookieBannerMedia.html")');
        return;
      }

      const externalMediaElementConfig = this.cookieBannerRenderer.data.cookieSettings.externalMedia.media[cookieName];
      const wrapper = externalMediaElement.closest(CsCookieBannerHandler.options.selector.externalMediaWrapper);

      if (externalMediaElementConfig.previewBox) {
        const previewBox = externalMediaElement.nextElementSibling;
        if (cookieString && cookieString.includes(cookieName)) {

          // show content
          this.showExternalMediaContent(externalMediaElement);

          // remove preview box
          if (previewBox && previewBox.classList.contains(CsCookieBannerHandler.options.selector.externalMediaPreviewBoxClass)) {
            previewBox.remove();
          }
          if (wrapper) wrapper.classList.remove(CsCookieBannerHandler.options.selector.externalMediaWrapperPreviewBoxIsVisibleClass);
        } else {

          // remove content
          this.removeExternalMediaContent(externalMediaElement);

          // create preview box
          if (!previewBox || !previewBox.classList.contains(CsCookieBannerHandler.options.selector.externalMediaPreviewBoxClass)) {
            this.cookieBannerRenderer.buildMediaPreviewBox(externalMediaElement, cookieName);
          }
          if (wrapper) wrapper.classList.add(CsCookieBannerHandler.options.selector.externalMediaWrapperPreviewBoxIsVisibleClass);
        }
      }

    });
  }

  customFunctions = {
    activateGoogleMapsExt: () => {
      const googleMapsDivs = document.querySelectorAll('div[data-cookie-name="maps_google"]');

      googleMapsDivs.forEach((div) => {
        const libraryUrl = div.dataset.library;

        if (libraryUrl) {
          const script = document.createElement('script');
          script.src = libraryUrl;
          document.body.appendChild(script);

          document.querySelector('.js-gme-container').removeAttribute('style');
        }
      });
    },
    activateSmartsupp: () => {
      const smartsuppDiv = document.querySelector('div[data-cookie-name="chat_smartsupp"]');

      if (smartsuppDiv) {
        const smartsuppCode = `
          var _smartsupp = _smartsupp || {};
          _smartsupp.key = '4b057508eb9b06f5cb0d4d8d087669bb0a8535a7';
          window.smartsupp || (function (d) {
            var s, c, o = smartsupp = function () { o._.push(arguments) }; o._ = [];
            s = d.getElementsByTagName('script')[0]; c = d.createElement('script');
            c.type = 'text/javascript'; c.charset = 'utf-8'; c.async = true;
            c.src = 'https://www.smartsuppchat.com/loader.js?';
            s.parentNode.insertBefore(c, s);
          })(document);
        `;

        const scriptEl = document.createElement('script');
        scriptEl.type = 'text/javascript';
        scriptEl.text = smartsuppCode;

        document.body.appendChild(scriptEl);

        // @TODO magnific was replaced by tobii
        // eslint-disable-next-line
        if (window.jQuery && $.magnificPopup) {
          // eslint-disable-next-line
          $.magnificPopup.close();
        }
      }
    },
    activateTrustYou: () => {
      const trustYouIframes = document.querySelectorAll('iframe[data-cookie-name="trust_you"]');

      trustYouIframes.forEach((iframe) => {
        const libraryUrl = iframe.dataset.library;

        // If library URL is present, load resizer
        if (libraryUrl) {
          const script = document.createElement('script');
          script.src = libraryUrl;
          document.body.appendChild(script);
        }

        // Wait a couple of milliseconds before calling the resize function,
        // because the iframe is loaded asynchronously
        setTimeout(() => {

          if (typeof iFrameResize === 'function') {
            // eslint-disable-next-line
            iFrameResize({ log: false }, iframe);
          } else {
            console.warn('iFrameResize function not found. Is the library loaded?');
          }
        }, 500);
      });
    },
    activateHotelSpider: () => {

      const hotelSpiderDiv = document.querySelector('div[data-cookie-name="hotelspider_cookie"]');

      if (hotelSpiderDiv) {
        // load CSS
        const linkHotelSpider = document.createElement('link');
        linkHotelSpider.rel = 'stylesheet';
        linkHotelSpider.href = 'https://wbe-static.hotel-spider.com/widget/spiderBooking4.css';
        linkHotelSpider.type = 'text/css';
        document.head.appendChild(linkHotelSpider);

        // load JS
        const scriptHotelSpider = document.createElement('script');
        scriptHotelSpider.src = 'https://wbe-static.hotel-spider.com/widget/spiderBooking4.js';
        scriptHotelSpider.async = true;
        scriptHotelSpider.onload = function () {
          // eslint-disable-next-line
          if (typeof jQuery.fn.SpiderBooking4 === 'function') {
            const spiderBookingContainer = document.getElementById('spiderBooking');
            if (spiderBookingContainer) {
              // eslint-disable-next-line
              jQuery(spiderBookingContainer).SpiderBooking4({
                hotelId: '02H5e00cb52afeb0',
                template: spiderBookingContainer.dataset.template,
                lang: spiderBookingContainer.dataset.lang
              });
            }
          }
        };
        document.head.appendChild(scriptHotelSpider);
      }
    }
  };

  loadExternalScript(url) {
    return new Promise((resolve, reject) => {
      // check if script is already loaded
      if (document.querySelector(`script[src="${url}"]`)) {
        resolve();
        return;
      }

      // create script element
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(s);
    });
  }

  initAdditionalFunctions() {
    const externalMediaElementConfig = this.cookieBannerRenderer.data.cookieSettings.externalMedia.media;
    const cookieString = Cookies.get(CsCookieBannerHandler.options.cookieName);

    Object.entries(externalMediaElementConfig).forEach(([cookieName, item]) => {
      if (item.function && this.customFunctions[item.function] && cookieString && cookieString.includes(cookieName)) {
        this.customFunctions[item.function]();
      }

      if (item.urlToScript && cookieString && cookieString.includes(cookieName)) {
        this.loadExternalScript(item.urlToScript).catch(err => console.warn(err));
      }
    });
  }



  /**
   * cookie persistence (cookie string handling)
   */
  saveCookies() {
    const checkboxes = document.querySelectorAll(CsCookieBannerHandler.options.selector.checkbox);
    const selectedKeys = [];

    checkboxes.forEach(cb => {
      if (cb.checked) {
        const id = cb.id || '';
        if (id.startsWith('cscb-')) {
          const key = id.replace('cscb-', '');
          selectedKeys.push(key);
        }
      }
    });

    if (selectedKeys.includes(this.options.gtmMainCategory) && !selectedKeys.includes(CsCookieBannerHandler.options.gtmCookieText)) {
      selectedKeys.push(CsCookieBannerHandler.options.gtmCookieText);
    }

    const cookieValue = `|${selectedKeys.join('|')}|`;

    Cookies.set(CsCookieBannerHandler.options.cookieName, cookieValue, {
      expires: CsCookieBannerHandler.options.expireDays,
      path: '/'
    });

    this.loadTagManager();

    this.refreshExternalMediaContent();

    this.initAdditionalFunctions();
  }



  /**
   * event handling (interaction logic)
   */
  handleContrastToggle() {
    const contrastButtonPageSelector = document.querySelector(CsCookieBannerHandler.options.selector.contrastButtonPage);
    if (!contrastButtonPageSelector) return;

    contrastButtonPageSelector.click();
  }

  handleCheckboxChange(target) {
    if (target.classList.contains(CsCookieBannerHandler.options.selector.checkboxParentClass)) {
      this.handleCheckboxChildrenStatePropagation(target);
    }

    if (target.classList.contains(CsCookieBannerHandler.options.selector.checkboxChildClass)) {
      this.handleCheckboxParentStatePropagation(target);
    }
  }

  acceptAllCookies() {
    this.activateAllCheckboxes();
    this.saveCookies();

    const cookieBannerLayer = document.querySelector(CsCookieBannerHandler.options.selector.cookieBannerLayer);
    if (cookieBannerLayer) {
      this.closeCookieBannerLayer();
    }
  }

  acceptExternalMediaCookie(trigger) {

    const cookieKey = trigger.dataset.csCookieKey;
    const checkboxId = `cscb-${cookieKey}`;
    const checkbox = document.getElementById(checkboxId);

    if (this.options.openModalOnExternalMediaFrame) {

      // open modal
      if (this.modalManager) {
        this.modalManager.open(CsCookieBannerHandler.options.modalName, false, trigger);
      }

      // trigger accordion item, if it is not expanded
      const accordionTrigger = document.getElementById(CsCookieBannerHandler.options.selector.externalMediaAccordionTriggerId);
      if (accordionTrigger && accordionTrigger.getAttribute('aria-expanded') === 'false') {
        accordionTrigger.click();
      }

      // scroll to checkbox
      checkbox.scrollIntoView({
        behavior: 'smooth'
      });

      // focus checkbox after accordion item has opened
      requestAnimationFrame(() => {
        checkbox.focus();
      });

    } else {

      // set checkbox and save cookies
      checkbox.checked = true;
      this.handleCheckboxChange(checkbox);
      this.saveCookies();
    }
  }

  declineAllCookies() {
    this.deactivateAllCheckboxes();
    this.saveCookies();

    const cookieBannerLayer = document.querySelector(CsCookieBannerHandler.options.selector.cookieBannerLayer);
    if (cookieBannerLayer) {
      this.closeCookieBannerLayer();
    }
  }

  customizeSettings(trigger) {

    // open modal
    if (this.modalManager) {
      this.modalManager.open(CsCookieBannerHandler.options.modalName, true, trigger);
    }
  }

  saveSettings() {
    this.saveCookies();
  }



  /**
   * event binding (listeners)
   */
  bindModalOpenEvent() {
    document.addEventListener('cs-modal-open', () => {

      // close cookie banner layer
      this.closeCookieBannerLayer(false);
    });
  }

  bindCloseEvent() {
    /* modal close is handled by modal manager,
     * open cookie banner layer if no cookies are set
     * and modal does not open on start
     * or it does open, but is on ignore list for this url
     */
    document.addEventListener('cs-modal-close', (e) => {
      const { name: modalName, triggerLink } = e.detail;
      if (modalName !== 'cscb-cookie-banner') return;

      // background layer (= modal) and close button have dismiss event
      // triggerLink is empty for escape key
      if (triggerLink?.dataset.cscbDismissEvent === 'true' || !triggerLink) {
        const cookieString = Cookies.get(CsCookieBannerHandler.options.cookieName);
        if (cookieString || (CsCookieBannerHandler.options.openModalOnStart && !this.isUrlOnIgnoreList())) return;

        this.openCookieBannerLayer();
      }
    });
  }

  bindContrastButton() {
    const contrastBtn = document.querySelector(CsCookieBannerHandler.options.selector.contrastButton);
    if (contrastBtn) {
      contrastBtn.addEventListener('click', () => {
        this.handleContrastToggle();
      });
    }
  }

  bindCheckboxEvents() {
    const checkboxes = document.querySelectorAll(CsCookieBannerHandler.options.selector.checkbox);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (event) => {
        this.handleCheckboxChange(event.target);
      });
    });
  }

  bindConsentButtons() {
    const acceptButtons = document.querySelectorAll(CsCookieBannerHandler.options.selector.acceptAllCookies);
    const declineButtons = document.querySelectorAll(CsCookieBannerHandler.options.selector.declineAllCookies);
    const customizeSettingsButton = document.querySelector(CsCookieBannerHandler.options.selector.customizeSettings);
    const saveSettingsBtn = document.querySelector(CsCookieBannerHandler.options.selector.saveSettings);
    const acceptExternalMediaFrame = document.querySelectorAll(CsCookieBannerHandler.options.selector.externalMediaWrapper);

    acceptExternalMediaFrame.forEach(acceptFrame => {
      acceptFrame.addEventListener('click', (e) => {
        if (e.target.classList.contains(CsCookieBannerHandler.options.selector.externalMediaAcceptButtonClass)) {
          this.acceptExternalMediaCookie(e.target);
        }
      });
    });

    acceptButtons.forEach(acceptBtn => {
      acceptBtn.addEventListener('click', () => {
        this.acceptAllCookies();
      });
    });

    declineButtons.forEach(declineBtn => {
      declineBtn.addEventListener('click', () => {
        this.declineAllCookies();
      });
    });

    customizeSettingsButton.addEventListener('click', () => {
      this.customizeSettings(customizeSettingsButton);
    });

    if (saveSettingsBtn) saveSettingsBtn.addEventListener('click', () => {
      this.saveSettings();
    });
  }

  bindEvents() {

    // modal events
    this.modalManager.bindCloseSelectors(document.querySelector(this.options.selector.cookieBannerModal));

    // specific cscb events
    this.bindCloseEvent();
    this.bindContrastButton();
    this.bindCheckboxEvents();
    this.bindConsentButtons();
    this.bindModalOpenEvent();
  }



  /**
   * display state for layer and modal
   */
  openCookieBannerModal() {
    if (this.modalManager) {
      this.modalManager.open(CsCookieBannerHandler.options.modalName);
    }
  }

  toggleCookieBannerLayer(stateOpen = true, setCloseFocus = true) {
    const cookieBannerLayer = document.querySelector(CsCookieBannerHandler.options.selector.cookieBannerLayer);
    if (!cookieBannerLayer) return;

    if (stateOpen) {

      // open layer
      cookieBannerLayer.classList.add(CsCookieBannerHandler.options.selector.showClass);
      requestAnimationFrame(() => {
        cookieBannerLayer.classList.add(CsCookieBannerHandler.options.selector.animationClass);
      });
    } else {

      // close layer
      cookieBannerLayer.classList.remove(CsCookieBannerHandler.options.selector.animationClass);
      setTimeout(() => {
        cookieBannerLayer.classList.remove(CsCookieBannerHandler.options.selector.showClass);
      }, CssPropertyHelper.getCssTimeProperty(CsCookieBannerHandler.options.cssVariables.cookieBannerAnimationTime));

      // focus first element in body, if set
      if (setCloseFocus) {
        const firstFocusableElement = document.querySelector(CsCookieBannerHandler.options.selector.firstFocusableElement);
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      }
    }

    // add or remove space
    this.reserveSpaceForCookieBannerLayer(stateOpen);
  }

  closeCookieBannerLayer(setFocus = true) {
    this.toggleCookieBannerLayer(false, setFocus);
  }

  openCookieBannerLayer() {
    this.toggleCookieBannerLayer();
  }

  openCookieBanner() {
    const cookieString = Cookies.get(CsCookieBannerHandler.options.cookieName);
    if (cookieString) return;

    if (CsCookieBannerHandler.options.openModalOnStart && !this.isUrlOnIgnoreList()) {
      this.openCookieBannerModal();
    } else {
      this.openCookieBannerLayer();
    }
  }



  /**
   * main init function
   */
  init() {
    document.addEventListener('cscb:built', () => {
      this.modalManager = PluginController.retrievePluginInstanceFromElement(document, 'CsModalManager');
      this.cookieBannerRenderer = PluginController.retrievePluginInstanceFromElement(this.el, 'CsCookieBannerRenderer');

      this.initializeCookieBannerContent();
      this.bindEvents();
      this.openCookieBanner();
      this.refreshExternalMediaContent();
      this.initAdditionalFunctions();

      this.loadGtmSettings().then(() => {
        this.loadTagManager();
      }).catch(error => {
        console.warn('Could not load Google Tag Manager keys.', error);
      });

      window.addEventListener('resize', () => {
        const layer = document.querySelector(CsCookieBannerHandler.options.selector.cookieBannerLayer);
        if (layer && layer.classList.contains(CsCookieBannerHandler.options.selector.showClass)) {
          this.reserveSpaceForCookieBannerLayer(true);
        }
      });

      document.dispatchEvent(new CustomEvent('cscb:initialized'));
    });
  }
}
